import type { IDBPDatabase, IDBPObjectStore, IDBPTransaction } from 'idb';

import type { DocStorageSchema } from '../schema';
import type {
  IndexMetadata,
  IndexPosting,
  IndexReadTransaction,
  IndexRecord,
  IndexWriteTransaction,
  MetadataReader,
  MetadataWriter,
  PostingKeyIndex,
  PostingNidIndex,
  PostingReader,
  PostingWriter,
  RecordIndex,
  RecordReader,
  RecordWriter,
  TableIndex,
} from './access';

type Stores = ('indexerRecords' | 'invertedIndex' | 'indexerMetadata')[];
type ReadMode = 'readonly' | 'readwrite';
type RecordStore = IDBPObjectStore<
  DocStorageSchema,
  Stores,
  'indexerRecords',
  ReadMode
>;
type PostingStore = IDBPObjectStore<
  DocStorageSchema,
  Stores,
  'invertedIndex',
  ReadMode
>;

// Select each concrete store/index before adapting its methods. Comparing the
// generic IDB objectStore/index functions directly loses their numeric key type.
function recordReader(store: RecordStore): RecordReader {
  function index(name: 'id'): RecordIndex;
  function index(name: 'table'): TableIndex;
  function index(name: 'id' | 'table'): RecordIndex | TableIndex {
    if (name === 'id')
      return { getKey: query => store.index('id').getKey(query) };
    return {
      getAllKeys: query => store.index('table').getAllKeys(query),
      count: query => store.index('table').count(query),
    };
  }
  return {
    get: key => store.get(key),
    getMany: keys => Promise.all(keys.map(key => store.get(key))),
    index,
  };
}
function postingReader(store: PostingStore): PostingReader {
  function index(name: 'key'): PostingKeyIndex;
  function index(name: 'nid'): PostingNidIndex;
  function index(name: 'key' | 'nid'): PostingKeyIndex | PostingNidIndex {
    if (name === 'key')
      return {
        getAll: (query, count) => store.index('key').getAll(query, count),
      };
    return { getAllKeys: query => store.index('nid').getAllKeys(query) };
  }
  return { index };
}

class ReadTransaction implements IndexReadTransaction {
  constructor(
    protected readonly transaction: IDBPTransaction<
      DocStorageSchema,
      Stores,
      ReadMode
    >
  ) {}
  objectStore(name: 'indexerRecords'): RecordReader;
  objectStore(name: 'invertedIndex'): PostingReader;
  objectStore(name: 'indexerMetadata'): MetadataReader;
  objectStore(
    name: Stores[number]
  ): RecordReader | PostingReader | MetadataReader {
    switch (name) {
      case 'indexerRecords':
        return recordReader(this.transaction.objectStore(name));
      case 'invertedIndex':
        return postingReader(this.transaction.objectStore(name));
      case 'indexerMetadata': {
        const store = this.transaction.objectStore(name);
        return { get: (key: string) => store.get(key) };
      }
    }
  }
}
class WriteTransaction
  extends ReadTransaction
  implements IndexWriteTransaction
{
  constructor(
    private readonly writable: IDBPTransaction<
      DocStorageSchema,
      Stores,
      'readwrite'
    >
  ) {
    super(writable);
  }
  override objectStore(name: 'indexerRecords'): RecordWriter;
  override objectStore(name: 'invertedIndex'): PostingWriter;
  override objectStore(name: 'indexerMetadata'): MetadataWriter;
  override objectStore(
    name: Stores[number]
  ): RecordWriter | PostingWriter | MetadataWriter {
    switch (name) {
      case 'indexerRecords': {
        const store = this.writable.objectStore(name);
        return {
          ...recordReader(store),
          put: (record: IndexRecord) => store.put(record),
          delete: key => store.delete(key),
          clear: () => store.clear(),
        };
      }
      case 'invertedIndex': {
        const store = this.writable.objectStore(name);
        return {
          ...postingReader(store),
          put: (posting: IndexPosting) => store.put(posting),
          delete: key => store.delete(key),
          clear: () => store.clear(),
        };
      }
      case 'indexerMetadata': {
        const store = this.writable.objectStore(name);
        return {
          get: (key: string) => store.get(key),
          put: (record: IndexMetadata) => store.put(record),
          clear: () => store.clear(),
        };
      }
    }
  }
}

/** Forward real IDB operations through the narrow algorithm port. */
export class IDBIndexerAccess {
  constructor(private readonly database: IDBPDatabase<DocStorageSchema>) {}
  readonly(): IndexReadTransaction {
    return new ReadTransaction(
      this.database.transaction(
        ['indexerRecords', 'invertedIndex', 'indexerMetadata'],
        'readonly',
        { durability: 'relaxed' }
      )
    );
  }
  readwrite(): IndexWriteTransaction {
    return new WriteTransaction(
      this.database.transaction(
        ['indexerRecords', 'invertedIndex', 'indexerMetadata'],
        'readwrite',
        { durability: 'relaxed' }
      )
    );
  }
}
